/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
// Chakra Components
import {
    AspectRatio,
    Box,
    Img,
    Flex,

    // Hooks
    ListItem,
    List,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {WebRotator} from '@consteed/frontend/components/web-rotator'

export {Skeleton} from '@salesforce/retail-react-app/app/components/image-gallery'

const EnterKeyNumber = 13

const LARGE = 'large'
const SMALL = 'small'

const HeroMedium = ({medium, loadingStrategy, heroImageMaxWidth}) => {
    switch (medium.type) {
        case 'video':
            // TODO: final version for the general product video poster required. It should be preloaded with worker
            // TODO: add tracks for screen readers (muted can perhaps be removed)
            // assume video is 16:9 aspect ratio => 9/16 = 0.5625 => padding takes free space 1-0.5625 = 0.4375 => py: 0.4375 / 2 = 0.21875
            return (
                <Box py={0.21875} backgroundColor={'black'}>
                    <video
                        key={medium.link}
                        controls={true}
                        poster={getAssetUrl('static/img/global/productVideoPoster.png')}
                        muted
                        playsInline={true}
                    >
                        <source src={medium.link} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </Box>
            )
        case '3DModel':
            // max width for 3D Model needed for tablet viewport as element will overlap with the thumbnail images otherwise
            // TODO: if initialisation of 3D model is on small viewport it does not scale up correctly on larger viewports,
            // basic problem could be that the 3D model plugin uses jquery to manipulate DOM elements that should be controlled by react
            // height is not always updated correctly by the 3D model plugin, it at least does not overlap with other elements anymore
            return (
                <Box maxWidth="800px" m="auto">
                    <WebRotator />
                </Box>
            )
        default:
            return (
                <DynamicImage
                    src={`${medium.disBaseLink || medium.link}[?sw={width}&q=60]`}
                    widths={{
                        base: '100vw',
                        lg: heroImageMaxWidth
                    }}
                    imageProps={{
                        alt: medium.alt,
                        loading: loadingStrategy
                    }}
                />
            )
    }
}

HeroMedium.propTypes = {
    medium: PropTypes.object.isRequired,
    loadingStrategy: PropTypes.string,
    heroImageMaxWidth: PropTypes.number
}

/**
 * The media gallery displays a hero image/video or 3D Model and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const MediaGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false,
    videos = [],
    show3DModel = false
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const styles = useMultiStyleConfig('ImageGallery', {size})
    const location = useLocation()

    // Get the 'hero' medium for the current variation.
    const heroMediaGroup = useMemo(() => {
        let tempMediaGroup = findImageGroupBy(imageGroups, {
            viewType: LARGE,
            selectedVariationAttributes
        })
        if (!tempMediaGroup) {
            tempMediaGroup = {
                images: []
            }
        }
        tempMediaGroup.media = tempMediaGroup.images.concat(videos)
        if (show3DModel) {
            tempMediaGroup.media.push({
                type: '3DModel',
                thumbnail: false
            })
        }
        return tempMediaGroup
    }, [selectedVariationAttributes, show3DModel])

    useEffect(() => {
        // reset the selected index when location search changes
        setSelectedIndex(0)
    }, [location.search])

    // Get a memoized image group used for the thumbnails. We use the `useMemo` hook
    // so we don't have to waste time filtering the image groups each render if the
    // selected variation attributes haven't changed.
    const thumbnailImageGroup = useMemo(() => {
        let tempThumbnails = findImageGroupBy(imageGroups, {
            viewType: SMALL,
            selectedVariationAttributes
        })
        if (!tempThumbnails) {
            tempThumbnails = {
                images: []
            }
        }
        tempThumbnails.media = tempThumbnails.images.concat(
            videos.map((video) => {
                return {
                    alt: video.alt, // TODO: add additional text for video alt
                    link: getAssetUrl('static/img/global/videoThumbnail.svg')
                }
            })
        )
        if (show3DModel) {
            tempThumbnails.media.push({
                alt: 'Thumbnail 3D Model', // TODO: add alt text for 3D Model
                link: getAssetUrl('static/img/global/360.jpg')
            })
        }
        return tempThumbnails
    }, [selectedVariationAttributes, show3DModel])

    const heroMedium = heroMediaGroup?.media?.[selectedIndex]
    const thumbnailImages = thumbnailImageGroup?.media || []
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    const heroImageMaxWidth = styles.heroImage.maxWidth[3] // in px

    return (
        <Flex direction="column">
            {heroMedium && (
                <Box {...styles.heroImageGroup}>
                    <AspectRatio {...styles.heroImage} ratio={1}>
                        <HeroMedium
                            medium={heroMedium}
                            heroImageMaxWidth={heroImageMaxWidth}
                            loadingStrategy={loadingStrategy}
                        />
                    </AspectRatio>
                </Box>
            )}

            <List display={'flex'} flexWrap={'wrap'}>
                {thumbnailImages.map((image, index) => {
                    const selected = index === selectedIndex
                    return (
                        <ListItem
                            {...styles.thumbnailImageItem}
                            key={index}
                            borderColor={`${selected ? 'black' : ''}`}
                            borderWidth={`${selected ? '1px' : 0}`}
                        >
                            <AspectRatio ratio={1}>
                                <Box
                                    as="button"
                                    aria-pressed={selected ? 'true' : 'false'}
                                    onKeyDown={(e) => {
                                        if (e.keyCode === EnterKeyNumber) {
                                            return setSelectedIndex(index)
                                        }
                                    }}
                                    onClick={() => setSelectedIndex(index)}
                                    data-testid="image-gallery-thumbnails"
                                >
                                    <Img
                                        alt={image.alt}
                                        src={image.disBaseLink || image.link}
                                        loading={loadingStrategy}
                                    />
                                </Box>
                            </AspectRatio>
                        </ListItem>
                    )
                })}
            </List>
        </Flex>
    )
}

MediaGallery.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Media gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool,
    videos: PropTypes.array,
    show3DModel: PropTypes.bool
}

export default MediaGallery
